import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BackLink, Button, ButtonGroup, ButtonLink, ErrorSummary, Radios } from '../../../components/idsk';
import { mergeDefinition, type FieldDef, type FormDefinition } from '../../../form-engine';
import { text } from '../../../form-engine/types';
import { usePageTitle } from '../../../layout/usePageTitle';
import { locate, moveCustomToSection, moveToSection, newOrderErrors, validateFor } from './definitionOps';
import type { BuilderMode } from './FieldEditor';

const TOP = '__top__';

export interface FieldMovePageProps {
  mode: BuilderMode;
  /** template mode: the draft; custom mode: the pinned template definition */
  definition: FormDefinition;
  customFields: FieldDef[];
  fieldKey: string;
  backTo: string;
  titleParts: string[];
  onSave: (next: { definition: FormDefinition; customFields: FieldDef[]; message: string }) => Promise<void>;
}

/**
 * Question page "Kam chcete presunúť pole …?": into a section (at its end) or out of the section, without dragging
 * (WCAG 2.5.7). Team members (custom fields) can only use their own sections. A move that would put a field before
 * a field its rule depends on is refused with an explanation.
 */
export function FieldMovePage({ mode, definition, customFields, fieldKey, backTo, titleParts, onSave }: FieldMovePageProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const effective = mode === 'template' ? definition : mergeDefinition(definition, customFields);
  const loc = locate(mode === 'template' ? definition.fields : customFields, fieldKey);
  const sections = (mode === 'template' ? definition.fields : customFields).filter((f) => f.type === 'SECTION');
  const current = loc?.parent?.key ?? TOP;
  const [choice, setChoice] = useState<string>(current);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const label = (f: FieldDef) => text(f.label, lang);
  const title = loc ? t('builder.move.title', { label: label(loc.field) }) : '';
  usePageTitle([title, ...titleParts]);
  if (!loc) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setCount((c) => c + 1);
    const target = choice === TOP ? null : choice;
    if ((loc.parent?.key ?? null) === target) {
      setError(t('builder.move.unchanged'));
      return;
    }
    let next: { definition: FormDefinition; customFields: FieldDef[] } | null = null;
    if (mode === 'template') {
      const moved = moveToSection(definition, fieldKey, target);
      if (moved) next = { definition: moved, customFields };
    } else {
      const moved = moveCustomToSection(customFields, fieldKey, target);
      if (moved) next = { definition, customFields: moved };
    }
    if (!next) return;
    const nextEffective = mode === 'template' ? next.definition : mergeDefinition(next.definition, next.customFields);
    const introduced = newOrderErrors(validateFor(mode, effective, definition, customFields), validateFor(mode, nextEffective, next.definition, next.customFields));
    if (introduced.length) {
      const dependent = locate(nextEffective.fields, introduced[0]!.field)?.field;
      setError(t('builder.move.blocked', { label: label(loc.field), dependent: dependent ? label(dependent) : introduced[0]!.field }));
      return;
    }
    const section = target ? sections.find((s) => s.key === target) : null;
    await onSave({
      ...next,
      message: section ? t('builder.move.movedInto', { label: label(loc.field), section: label(section) }) : t('builder.move.movedOut', { label: label(loc.field) }),
    });
  };

  return (
    <>
      <BackLink to={backTo} />
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          {error && <ErrorSummary errors={[{ targetId: 'move-target', text: error }]} focusKey={count} />}
          <form onSubmit={(e) => void submit(e)} noValidate>
            <Radios
              name="move-target"
              idPrefix="move-target"
              legend={title}
              legendClassName="govuk-fieldset__legend--l"
              legendIsPageHeading
              hint={t('builder.move.hint')}
              error={error ?? undefined}
              items={[
                { value: TOP, label: t('builder.move.top'), hint: t('builder.move.topHint') },
                ...sections.map((s) => ({ value: s.key, label: label(s) })),
              ]}
              value={choice}
              onChange={setChoice}
            />
            <ButtonGroup>
              <Button type="submit" preventDoubleClick>
                {t('builder.move.submit')}
              </Button>
              <ButtonLink to={backTo} variant="secondary">
                {t('common.cancel')}
              </ButtonLink>
            </ButtonGroup>
          </form>
        </div>
      </div>
    </>
  );
}
